import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Grid,
  Container,
  Divider,
  Chip,
  CircularProgress,
  IconButton,
  Alert,
  Snackbar
} from '@mui/material';
import {
  ShoppingCart,
  School,
  List,
  StickyNote2,
  Add,
  Delete,
  Send
} from '@mui/icons-material';
import { vendorOrderService } from './services/vendorService';

// Mock school data (like original)
const mockSchoolData = {
  schoolName: "",
  address: "",
  contactPerson: "",
  email: "",
  phone: ""
};

// Order form component matching original VendorPortal
function VendorOrderForm() {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    schoolName: mockSchoolData.schoolName,
    address: mockSchoolData.address,
    contactPerson: mockSchoolData.contactPerson,
    email: mockSchoolData.email,
    phone: mockSchoolData.phone,
    items: [{ itemName: "", quantity: 1 }],
    notes: ""
  });

  // Get school details from Redux
  const schooldetails_ = useSelector((state) => state.schooldetails);
  const schooldetails = schooldetails_[0];
  console.log(schooldetails);
  console.log("schooldetails?.schoolID");
  const schoolID = schooldetails?.schoolID || 'default'; 

  useEffect(() => {
    // Load actual school details if available
    const schooldetails = JSON.parse(localStorage.getItem("schoolDetails") || "{}");
    if (schooldetails.schoolName) {
      setFormData(prev => ({
        ...prev,
        schoolName: schooldetails.schoolname || mockSchoolData.schoolName,
        address: schooldetails.address || mockSchoolData.address,
        contactPerson: schooldetails.contact|| mockSchoolData.contactPerson,
        email: schooldetails.email || mockSchoolData.email,
        phone: schooldetails.contact || mockSchoolData.phone
      }));
    }
  }, []);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.schoolname.trim()) newErrors.schoolname = 'School name is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.contact.trim()) newErrors.contact = 'Contact person is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.contact.trim()) newErrors.contact = 'Phone is required';
    
    if (formData.items.length === 0) {
      newErrors.items = 'At least one item is required';
    } else {
      formData.items.forEach((item, index) => {
        if (!item.itemName.trim()) {
          newErrors[`item_${index}`] = 'Item name is required';
        }
        if (item.quantity < 1) {
          newErrors[`quantity_${index}`] = 'Quantity must be at least 1';
        }
      });
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setMessage('Please fix the errors before submitting');
      setMessageType('error');
      setOpenSnackbar(true);
      return;
    }

    setLoading(true);
    try {
      const response = await vendorOrderService.submitOrder({
        ...formData,
        schoolID,
        submittedBy: "admin"
      });
      
      if (response.success) {
        setMessage(`Order submitted successfully! Order ID: ${response.orderId}`);
        setMessageType('success');
        setOpenSnackbar(true);
        
        // Reset form
        setFormData({
          schoolName: mockSchoolData.schoolName,
          address: mockSchoolData.address,
          contactPerson: mockSchoolData.contactPerson,
          email: mockSchoolData.email,
          phone: mockSchoolData.phone,
          items: [{ itemName: "", quantity: 1 }],
          notes: ""
        });
      }
    } catch (error) {
      console.error("Error submitting order:", error);
      setMessage("Error submitting order. Please try again.");
      setMessageType('error');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      schoolName: mockSchoolData.schoolName,
      address: mockSchoolData.address,
      contactPerson: mockSchoolData.contactPerson,
      email: mockSchoolData.email,
      phone: mockSchoolData.phone,
      items: [{ itemName: "", quantity: 1 }],
      notes: ""
    });
    setErrors({});
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { itemName: "", quantity: 1 }]
    }));
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={messageType} sx={{ width: '100%' }}>
          {message}
        </Alert>
      </Snackbar>

      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          shop with us
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Submit your purchase order request
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* School Information */}
        <Card elevation={3}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <School sx={{ color: 'primary.main', mr: 1.5 }} />
              <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
                School Information
              </Typography>
            </Box>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="School Name"
                  value={formData.schoolName}
                  onChange={(e) => setFormData(prev => ({ ...prev, schoolName: e.target.value }))}
                  error={!!errors.schoolName}
                  helperText={errors.schoolName}
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="School Address"
                  multiline
                  rows={2}
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  error={!!errors.address}
                  helperText={errors.address}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Person"
                  value={formData.contactPerson}
                  onChange={(e) => setFormData(prev => ({ ...prev, contactPerson: e.target.value }))}
                  error={!!errors.contactPerson}
                  helperText={errors.contactPerson}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  error={!!errors.email}
                  helperText={errors.email}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  required
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Order Items */}
        <Card elevation={3}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <List sx={{ color: 'primary.main', mr: 1.5 }} />
              <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
                Order Items
              </Typography>
            </Box>
            
            {formData.items.map((item, index) => (
              <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={`Item ${index + 1} Name`}
                      value={item.itemName}
                      onChange={(e) => updateItem(index, 'itemName', e.target.value)}
                      error={!!errors[`item_${index}`]}
                      helperText={errors[`item_${index}`]}
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={8} sm={4}>
                    <TextField
                      fullWidth
                      label="Quantity"
                      type="number"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                      error={!!errors[`quantity_${index}`]}
                      helperText={errors[`quantity_${index}`]}
                      inputProps={{ min: 1 }}
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={4} sm={2}>
                    {formData.items.length > 1 && (
                      <IconButton 
                        onClick={() => removeItem(index)}
                        color="error"
                        size="large"
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              </Box>
            ))}
            
            <Button
              startIcon={<Add />}
              onClick={addItem}
              variant="outlined"
              sx={{ mt: 2 }}
            >
              Add Another Item
            </Button>
          </CardContent>
        </Card>

        {/* Additional Notes */}
        <Card elevation={3}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <StickyNote2 sx={{ color: 'primary.main', mr: 1.5 }} />
              <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
                Additional Notes
              </Typography>
            </Box>
            
            <TextField
              fullWidth
              label="Special Instructions or Notes (Optional)"
              multiline
              rows={4}
              inputProps={{ maxLength: 500 }}
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Enter any special instructions, delivery requirements, or additional notes..."
              error={!!errors.notes}
              helperText={errors.notes || `${formData.notes.length}/500`}
            />
          </CardContent>
        </Card>

        {/* Form Actions */}
        <Card elevation={3}>
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'flex-end' }}>
              <Button
                onClick={handleCancel}
                disabled={loading}
                variant="outlined"
              >
                Cancel
              </Button>
              
              <Button
                onClick={handleSubmit}
                disabled={loading}
                variant="contained"
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Send />}
              >
                {loading ? 'Submitting...' : 'Submit Order'}
              </Button>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Chip
                icon={<School />}
                label="Emails will be sent to both the school and support@sisrevolution.com"
                variant="outlined"
                color="primary"
                size="small"
              />
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}

export default VendorOrderForm; 